import { useEffect } from 'react';

const useScrollAnimation = (ref, options = {}) => {
    const {
        type: animation = 'none',
        duration = '0.6s',
        delay = '0s',
        easing = 'ease-out',
        threshold = 0.15,
        rootMargin = '0px 0px -50px 0px',
        repeat = false,
        stagger = false,
        staggerDelay = '0.1s'
    } = options;

    useEffect(() => {
        const element = ref.current;
        if (!element || animation === 'none') return;

        // Add initial hidden class
        element.classList.add('animate-hidden');

        // Determine observer root (window for live, iframe document for CMS editor)
        let observerRoot = null;
        try {
            if (window.self !== window.top) {
                // Inside an iframe (CMS editor) — observe within iframe
                observerRoot = element.closest('.cms-webpage') || null;
            }
        } catch (e) {
            // Cross-origin — use default
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Set animation properties
                    element.style.animationDuration = duration;
                    element.style.animationDelay = delay;
                    element.style.animationTimingFunction = easing;

                    // Handle stagger for child elements
                    if (stagger) {
                        const children = element.querySelectorAll('.seczone-element, .seczone-layout');
                        children.forEach((child, index) => {
                            child.style.animationDuration = duration;
                            child.style.animationDelay = `calc(${delay} + ${index} * ${staggerDelay})`;
                            child.style.animationTimingFunction = easing;
                            child.classList.add('animate-hidden');
                            // Trigger child animation shortly after parent
                            requestAnimationFrame(() => {
                                child.classList.remove('animate-hidden');
                                child.classList.add('animate-visible', `animate-${animation}`);
                            });
                        });
                    }

                    // Remove hidden, add visible with animation type
                    element.classList.remove('animate-hidden');
                    element.classList.add('animate-visible', `animate-${animation}`);

                    if (!repeat) {
                        observer.unobserve(element);
                    }
                } else if (repeat) {
                    element.classList.remove('animate-visible', `animate-${animation}`);
                    element.classList.add('animate-hidden');
                }
            },
            {
                root: observerRoot,
                threshold,
                rootMargin
            }
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [ref, animation, duration, delay, easing, threshold, rootMargin, repeat, stagger, staggerDelay]);
};

export default useScrollAnimation;